import React, { useEffect, useState } from "react";
import { Box, TextField, Button, Typography, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, MenuItem } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { handleSave } from "../utils/handleSave";
import { createTempId } from "../utils/createTempId";
import { TABLE_SCROLL_CONTAINER_SX, hasValue, isRowComplete } from "../utils/formHelpers";
import { useKotFormDirty } from "../utils/useKotFormDirty";

const LICENSED_FAMILY_REQUIRED_FIELDS = ["familyNo", "licensedTo", "contactName", "emailId", "notes", "partyRelationship"];
const licensedFamilyRowHasData = (row) => LICENSED_FAMILY_REQUIRED_FIELDS.some((field) => hasValue(row?.[field]));

const LicensedFamiliesSection = ({ mode }) => {
  const isView = mode === "view";
  const dispatch = useDispatch();
  const savedData = useSelector((state) => state.kotClient.licensedFamilies);
  const clientId = useSelector((state) => state.kotClient?.clientId);
  // With nothing saved, edit mode still seeds one blank row so there is somewhere to type.
  // View mode must not: a read-only section with no data would otherwise render a row of
  // empty disabled inputs rather than saying it is empty.
  const buildRows = (nextRows = []) => {
    if (nextRows.length > 0) {
      return nextRows.map((row) => ({ ...row }));
    }

    if (isView) {
      return [];
    }

    return [
      {
        id: createTempId(),
        partyRelationship: "",
        familyNo: "",
        licensedTo: "",
        contactName: "",
        emailId: "",
        notes: "",
      },
    ];
  };

  const [selectedRows, setSelectedRows] = useState([]);

  const [rows, setRows] = useState(buildRows(savedData));
  const { isDirty, markDirty, markSaved, markReset, syncClean } = useKotFormDirty();

  useEffect(() => {
    setRows(buildRows(savedData));
    setSelectedRows([]);
    syncClean();
  }, [clientId, savedData]);

  const handleSelectRow = (id) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  };

  const deleteSelectedRows = () => {
    markDirty();
    setRows((prev) => prev.filter((row) => !selectedRows.includes(row.id)));
    setSelectedRows([]);
  };

  const updateRowField = (id, field, value) => {
    markDirty();
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const [isSaving, setIsSaving] = useState(false);

  const addRow = () => {
    // Allow a new row only when existing rows have all mandatory fields filled.
    if (rows.some((row) => !isRowComplete(row, LICENSED_FAMILY_REQUIRED_FIELDS))) {
      toast.warn("Complete the mandatory fields in the current row before adding another.");
      return;
    }

    markDirty();
    setRows((prev) => [
      ...prev,
      {
        id: createTempId(),
        partyRelationship: "",
        familyNo: "",
        licensedTo: "",
        contactName: "",
        emailId: "",
        notes: "",
      },
    ]);
  };

  const saveData = async () => {
    // Validate mandatory fields on submit for any started row (empty rows are ignored).
    if (rows.some((row) => licensedFamilyRowHasData(row) && !isRowComplete(row, LICENSED_FAMILY_REQUIRED_FIELDS))) {
      toast.error("Please complete all mandatory fields before saving.");
      return;
    }

    setIsSaving(true);
    try {
      const result = await handleSave(dispatch, "licensedFamilies", rows, clientId, savedData);
      if (result?.status !== "error") {
        markSaved();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    markReset();
    setRows(buildRows(savedData));
    setSelectedRows([]);
  };

  return (
    <Box p={4} bgcolor="#fff" borderRadius={2}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h3">Related Parties</Typography>
        {!isView && selectedRows.length > 0 && (
          <Button variant="contained" color="error" onClick={deleteSelectedRows}>
            Delete ({selectedRows.length})
          </Button>
        )}
      </Box>

      <Box sx={TABLE_SCROLL_CONTAINER_SX}>
        <Table sx={{ minWidth: 820, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {!isView && (
                <TableCell padding="checkbox">
                  <Checkbox data-ignore-dirty="true" checked={selectedRows.length === rows.length && rows.length > 0} indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length} onChange={(e) => setSelectedRows(e.target.checked ? rows.map((r) => r.id) : [])} size="medium" />
                </TableCell>
              )}
              <TableCell>Family No(s)</TableCell>
              <TableCell>Party Relationship</TableCell>
              <TableCell>Party Name</TableCell>
              <TableCell>Contact Name</TableCell>
              <TableCell>Email Address</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Reachable in view mode whenever the section has no data, and in edit mode
                once every row has been deleted. */}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6 + (isView ? 0 : 1)} align="center" sx={{ py: 5, color: "text.secondary", borderBottom: "none" }}>
                  <Typography>No records to show</Typography>
                </TableCell>
              </TableRow>
            )}

            {rows.map((row) => (
              <TableRow key={row.id}>
                {!isView && (
                  <TableCell padding="checkbox">
                    <Checkbox data-ignore-dirty="true" checked={selectedRows.includes(row.id)} onChange={() => handleSelectRow(row.id)} size="medium" />
                  </TableCell>
                )}

                {/* Family No */}
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField disabled={isView} size="small" fullWidth multiline minRows={1} placeholder="Family No(s)" value={row.familyNo} onChange={(e) => updateRowField(row.id, "familyNo", e.target.value)} />
                </TableCell>

                {/* Party Relationship */}
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField disabled={isView} size="small" fullWidth select label="Select Party Relationship" name="partyRelationship" value={row.partyRelationship} onChange={(e) => updateRowField(row.id, "partyRelationship", e.target.value)}>
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="Applicant">Applicant</MenuItem>
                    <MenuItem value="Assignee">Assignee</MenuItem>
                    <MenuItem value="Assignor">Assignor</MenuItem>
                    <MenuItem value="Co-owner/Joint Applicant">Co-owner/Joint Applicant</MenuItem>
                    <MenuItem value="Licensor">Licensor</MenuItem>
                    <MenuItem value="Licensee (exclusive or non-exclusive)">Licensee (exclusive or non-exclusive)</MenuItem>
                    <MenuItem value="Federal Funding Agency">Federal Funding Agency</MenuItem>
                    <MenuItem value="Employer">Employer</MenuItem>
                    <MenuItem value="Parent Company/Subsidiary">Parent Company/Subsidiary</MenuItem>
                    <MenuItem value="Other Entity">Other Entity</MenuItem>
                  </TextField>
                </TableCell>

                {/* Party Name */}
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField disabled={isView} size="small" fullWidth multiline minRows={1} placeholder="Party Name" value={row.licensedTo} onChange={(e) => updateRowField(row.id, "licensedTo", e.target.value)} />
                </TableCell>

                {/* Contact Name */}
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField disabled={isView} size="small" fullWidth multiline minRows={1} placeholder="Contact Name" value={row.contactName} onChange={(e) => updateRowField(row.id, "contactName", e.target.value)} />
                </TableCell>

                {/* Email */}
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField disabled={isView} size="small" fullWidth multiline minRows={1} placeholder="Email Address" value={row.emailId} onChange={(e) => updateRowField(row.id, "emailId", e.target.value)} />
                </TableCell>

                {/* Notes */}
                <TableCell sx={{ verticalAlign: "top" }}>
                  <TextField disabled={isView} size="small" fullWidth multiline minRows={1} placeholder="Notes" value={row.notes} onChange={(e) => updateRowField(row.id, "notes", e.target.value)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Add Row */}
      {!isView && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="outlined" onClick={addRow}>
            Add More
          </Button>
        </Box>
      )}

      {/* Actions — disabled until the form has unsaved changes */}
      {!isView && (
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="text" onClick={handleCancel} disabled={isSaving || !isDirty}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveData} disabled={isSaving || !isDirty}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LicensedFamiliesSection;
